import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Image, Mic, Square, Upload, Video, X } from 'lucide-react';
import { normalizeUploadFile } from '../lib/normalizeUploadFile';
import { extFromRecorderMime, formatRecMmSs, pickRecordingMimeType } from '../lib/voiceRecording';

/**
 * Pick image / video / document / audio (+ voice record) same pattern as task attachments.
 * @param {{ t: (key: string) => string; disabled?: boolean; onFileReady: (file: File) => void }} props
 */
export function MessageComposerAttachments({ t, disabled = false, onFileReady }) {
  const fileInputRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const audioModalOpenRef = useRef(false);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [fileAccept, setFileAccept] = useState('*/*');
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [audioRecState, setAudioRecState] = useState('idle');
  const [audioRecErr, setAudioRecErr] = useState('');
  const [audioRecSecs, setAudioRecSecs] = useState(0);

  useEffect(() => {
    audioModalOpenRef.current = audioModalOpen;
  }, [audioModalOpen]);

  useEffect(() => {
    if (audioRecState !== 'recording') return undefined;
    const id = setInterval(() => setAudioRecSecs((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [audioRecState]);

  const closeAudioModal = useCallback(() => {
    audioModalOpenRef.current = false;
    setAudioModalOpen(false);
    setAudioRecState('idle');
    setAudioRecErr('');
    setAudioRecSecs(0);

    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;

    const mr = mediaRecorderRef.current;
    if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
      mr.stop();
    } else {
      chunksRef.current = [];
      mediaRecorderRef.current = null;
    }
  }, []);

  const pushFile = useCallback(
    (/** @type {File | undefined} */ f) => {
      if (!f) return;
      onFileReady(normalizeUploadFile(f));
    },
    [onFileReady],
  );

  const startVoiceRecording = useCallback(async () => {
    setAudioRecErr('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setAudioRecErr(t('recordingNotSupported'));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mime = pickRecordingMimeType();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        streamRef.current?.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;

        const stillOpen = audioModalOpenRef.current;
        const recorder = mediaRecorderRef.current;

        if (!stillOpen) {
          chunksRef.current = [];
          mediaRecorderRef.current = null;
          return;
        }

        const blobType = recorder?.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;

        if (blob.size === 0) {
          setAudioRecErr(t('emptyRecording'));
          return;
        }

        audioModalOpenRef.current = false;
        setAudioModalOpen(false);
        setAudioRecState('idle');
        setAudioRecSecs(0);

        const fname = `voice-${Date.now()}${extFromRecorderMime(blobType)}`;
        const recorded = new File([blob], fname, { type: blobType });
        pushFile(recorded);
      };

      mediaRecorderRef.current = mr;
      setAudioRecSecs(0);
      mr.start(250);
      setAudioRecState('recording');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/NotAllowed|Permission|denied/i.test(msg)) {
        setAudioRecErr(t('micDenied'));
      } else {
        setAudioRecErr(msg);
      }
    }
  }, [pushFile, t]);

  const stopVoiceRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr?.state === 'recording') mr.stop();
  }, []);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={fileAccept}
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          pushFile(e.target.files?.[0]);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />
      <input
        ref={audioFileInputRef}
        type="file"
        accept="audio/mpeg,.mp3,audio/wav,audio/x-wav,audio/webm,audio/mp4,audio/x-m4a,.m4a,.aac,audio/aac,.ogg,audio/ogg"
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          pushFile(e.target.files?.[0]);
          closeAudioModal();
          if (audioFileInputRef.current) audioFileInputRef.current.value = '';
        }}
      />

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setFileAccept('image/png,image/jpeg,image/webp,image/gif,.webp');
            queueMicrotask(() => fileInputRef.current?.click());
          }}
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300 disabled:opacity-50"
        >
          <Image size={11} /> {t('composerImage')}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setFileAccept('video/mp4,video/webm,video/quicktime,video/*,.mp4,.mov,.mkv,.webm,.m4v');
            queueMicrotask(() => fileInputRef.current?.click());
          }}
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300 disabled:opacity-50"
        >
          <Video size={11} /> {t('composerVideo')}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setAudioRecErr('');
            audioModalOpenRef.current = true;
            setAudioModalOpen(true);
          }}
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300 disabled:opacity-50"
        >
          <Mic size={11} /> {t('composerAudio')}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setFileAccept('*/*');
            queueMicrotask(() => fileInputRef.current?.click());
          }}
          className="inline-flex items-center gap-1 rounded-md border border-brand-200 px-2 py-1 text-[11px] text-slate-600 dark:border-brand-900 dark:text-slate-300 disabled:opacity-50"
        >
          <FileText size={11} /> {t('composerDocument')}
        </button>
      </div>

      {audioModalOpen && (
        <div
          className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => closeAudioModal()}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-brand-200 bg-white p-5 shadow-2xl dark:border-brand-900 dark:bg-slate-950"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-white">{t('audioAttachmentTitle')}</h4>
              <button
                type="button"
                aria-label={t('close')}
                onClick={() => closeAudioModal()}
                className="rounded-lg p-1 text-slate-500 hover:bg-brand-100 dark:text-slate-400 dark:hover:bg-brand-900/30"
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{t('audioAttachmentSubtitle')}</p>

            {!!audioRecErr && (
              <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-700 dark:bg-red-900/25 dark:text-red-300">
                {audioRecErr}
              </p>
            )}

            {audioRecState === 'idle' ? (
              <div className="mt-4 flex flex-col gap-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-brand-200 py-3 text-sm font-medium text-slate-800 hover:bg-brand-50 dark:border-brand-800 dark:text-slate-100 dark:hover:bg-brand-950/50"
                  onClick={() => queueMicrotask(() => audioFileInputRef.current?.click())}
                >
                  <Upload size={18} strokeWidth={1.75} />
                  {t('uploadAudioFile')}
                </button>
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-500 py-3 text-sm font-semibold text-white shadow-md shadow-brand"
                  onClick={() => void startVoiceRecording()}
                >
                  <Mic size={18} strokeWidth={1.75} />
                  {t('recordVoiceNote')}
                </button>
              </div>
            ) : (
              <div className="mt-5 flex flex-col items-center gap-4">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg shadow-red-500/30 animate-pulse">
                  <Mic size={36} strokeWidth={1.5} />
                </div>
                <p className="font-mono text-lg font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatRecMmSs(audioRecSecs)}
                </p>
                <p className="text-center text-[11px] text-slate-500 dark:text-slate-400">{t('recordingTapStop')}</p>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white dark:bg-slate-200 dark:text-slate-900"
                  onClick={() => stopVoiceRecording()}
                >
                  <Square size={14} fill="currentColor" />
                  {t('stopRecording')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
